import { forwardRef } from 'preact/compat';
import { useImperativeHandle, useRef, useState } from 'preact/hooks';
import { useConfigContext } from './ConfigContext';
import { Close } from './icons/Close';
import { LightLabsLogo } from './icons/LightLabsLogo';
import { Share } from './icons/Share';
import { Product } from './types';
import { useProductVariant } from './useProductVariant';
import { deleteSearchParam, setSearchParam } from './utils/url-helpers';
import { Toast, ToastRef } from './Toast';

type Props = {
  product: Product;
};

export type WidgetDialogRef = {
  open: () => void;
  close: () => void;
};

export const WidgetDialog = forwardRef<WidgetDialogRef, Props>(({ product }: Props, ref) => {
  const [isIframeVisible, setIsIframeVisible] = useState(false);

  const { apiUrl, companyId } = useConfigContext();

  const variant = useProductVariant();

  const dialogRef = useRef<HTMLDialogElement>(null);

  const toastRef = useRef<ToastRef>(null);

  const open = () => {
    dialogRef.current?.showModal();
    setIsIframeVisible(true);
    setSearchParam('ll-open', '1');
  };

  const close = () => {
    dialogRef.current?.close();
    deleteSearchParam('ll-open');
    setTimeout(() => setIsIframeVisible(false), 200);
  };

  useImperativeHandle(ref, () => ({ open, close }));

  const share = () => {
    navigator.clipboard.writeText(window.location.href);
    toastRef.current?.open('Share link copied to clipboard');
  };

  const sku = product.skus.find((sku) => sku.shopifyVariantId === variant);

  return (
    <dialog className="dialog" ref={dialogRef}>
      <div className="backdrop" onClick={close}>
        <div className="content" onClick={(e) => e.stopPropagation()}>
          <header>
            <div className="close-button" onClick={close}>
              <Close width={20} height={20} stroke="#364153" />
            </div>

            <LightLabsLogo
              width={128}
              height={25}
              style={{ cursor: 'pointer' }}
              onClick={() =>
                window.open(
                  'https://lightlabs.com/?utm_medium=modal&utm_campaign=pip_logo_click' +
                    `&utm_source=${window.location.hostname}` +
                    `&utm_content=${companyId}_${product.id}`,
                )
              }
            />

            <div className="share-button" onClick={share}>
              <Share width={14} height={13} />
            </div>
          </header>

          {isIframeVisible && (
            <iframe
              src={`${apiUrl}/comprehensive-testing-report/${companyId}/${product.id}?hide_header=1&sku_id=${sku?.id || ''}`}
            />
          )}

          <Toast ref={toastRef} />
        </div>
      </div>
    </dialog>
  );
});
